import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Session, Project } from '@claude-code-api/shared';
import { Button } from '@/components/ui/button';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { motion } from 'motion/react';

const RUNNER_LABEL_KEYS: Record<string, string> = {
  'claude-code': 'claudeCode',
  'gift-ui': 'giftUi',
  acpx: 'acpx',
  'acpx-experimental': 'acpxExperimental',
  'acpx-team': 'acpxTeam',
};

function formatElapsedTime(createdAt: Date): string {
  const now = new Date();
  const diff = now.getTime() - new Date(createdAt).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

interface ActiveSessionBannerProps {
  sessions: Session[];
  projects: Project[];
}

export function ActiveSessionBanner({
  sessions,
  projects,
}: ActiveSessionBannerProps) {
  const { t } = useTranslation('dashboard');
  const primarySession = sessions[0];
  if (!primarySession) return null;

  const project = projects.find((p) => p.id === primarySession.projectId);
  const projectName = project?.name || t('unknownProject');
  const runnerKey = RUNNER_LABEL_KEYS[primarySession.runnerType];
  const runnerLabel = runnerKey
    ? t(`startSessionModal.runners.${runnerKey}.label`)
    : primarySession.runnerType;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      role="region"
      aria-label={t('activeSession.regionLabel')}
      className="rounded-lg border border-success/20 bg-success/5 p-4 flex items-center justify-between"
    >
      <div className="flex items-center gap-3">
        <StatusIndicator status="running" size="lg" />
        <div>
          <p className="text-sm font-semibold">{t('activeSession.title')}</p>
          <p className="text-sm text-muted-foreground">
            {t('activeSession.summary', {
              projectName,
              runnerLabel,
              elapsed: formatElapsedTime(primarySession.createdAt),
            })}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {sessions.length > 1 && (
          <Link
            to="/sessions"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('activeSession.moreRunning', { count: sessions.length - 1 })}
          </Link>
        )}
        <Button asChild>
          <Link to={`/sessions/${primarySession.id}`}>
            {t('activeSession.continueCoding')}
          </Link>
        </Button>
      </div>
    </motion.div>
  );
}
